import ColorSwatches from '@/components/ColorSwatches';
import StarRating from '@/components/StarRating';
import { ACTIVE_DESIGN_DEFAULT, GRAPHIC_TYPES } from '@/constants/customization.constants';
import { AppState } from '@/store';
import { PreviewRenderCanvas } from '@c2s-npm/plumreact';
import MoreColorsSwatch from '@components/tailwind/MoreColorsSwatch';
import Ribbon from '@components/tailwind/Ribbon';
import { TCSPreview, TPartnerProductCard, TPickerVariantType } from '@types';
import { formatCurrency } from '@utils/formatters.util';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import MyRTICPersonalize from './MyRTICPersonalize';

type Props = {
  product: TPartnerProductCard;
  rel?: string;
};

const MAX_SWATCHES_SHOWN = 5;

const withFee = (rate, fee) => {
  //? adding EGP to the regex to handle the case where the rate is already formatted with EGP
  return rate
    .replaceAll(/[$,EGP]/g, '')
    .split(/-/g)
    .map((r) => formatCurrency(parseFloat(r) + (fee || 0)))
    .join(' - ');
};

const ProductCard = ({ product, rel }: Props) => {
  const [graphicType, setGraphicType] = useState(GRAPHIC_TYPES.ENGRAVED.value);
  const {
    activeAsset,
    partnerData: { partner },
    selectionHistory: { colors: colorsHistory },
    refData: { faceFee, fees },
  } = useSelector((state: AppState) => state.partner);

  const [activeVariant, setActiveVariant] = useState<TPickerVariantType>(null);
  const onSwatchClick = (swatchData) => () => {
    if (swatchData === activeVariant) {
      setActiveVariant(null);
    } else {
      setActiveVariant(swatchData);
    }
  };

  const activeItem: TPartnerProductCard = activeVariant?.item || product;

  const activePreview: TCSPreview =
    activeVariant?.preview ||
    product.pickerVariants.colors.find((v) => v.url === product.variantUrl)?.preview ||
    product.preview;

  useEffect(() => {
    if (colorsHistory?.length > 0) {
      const lastSelectedColor = colorsHistory[0];
      const color = product.pickerVariants.colors.find((c) => c.id === lastSelectedColor);

      if (color) {
        setActiveVariant(color);
      }
    }
  }, [colorsHistory]);

  const { ourPriceFormatted, currentRateFormatted, isDiscounted, rating, reviewCount, itemUrl } =
    useMemo(() => {
      const qs = [partner && `partnerId=${partner.id}`, activeAsset && `assetId=${activeAsset.id}`]
        .filter((v) => !!v)
        .join('&');

      const baseUrl = activeItem.variantUrl || activeItem.url;
      const url = baseUrl + (baseUrl.indexOf('?') > 0 ? '&' : '?') + qs;

      const fee = (faceFee || 0) + (fees.find((f) => f.id === activeAsset?.feeId)?.rate || 0);

      return {
        ourPriceFormatted: withFee(product.ourPriceFormatted, fee),
        currentRateFormatted: withFee(product.salePriceFormatted ?? product.ourPriceFormatted, fee),
        isDiscounted: product.maxAdditionalSalePercent ?? product.salePriceFormatted,
        rating: product.item.reviewSummary.rating ?? product.item.reviewSummary.yotpoRating,
        reviewCount: product.item.reviewSummary.totalCount ?? product.item.reviewSummary.yotpoCount,
        itemUrl: url,
      };
    }, [activeItem, product, faceFee, fees, activeAsset]);

  return (
    <div className='tw-relative tw-h-full'>
      <div className='tw-h-full tw-w-full tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-py-3 tw-px-4'>
        <div className='tw-mb-1 tw-flex tw-items-center tw-justify-between tw-gap-8'>
          <a
            href={itemUrl}
            {...{ rel }}
            className='tw-mb-0 tw-block tw-text-lg tw-font-bold tw-text-body !tw-no-underline hover:tw-text-body'>
            {product.item.displayNameOnPage}
          </a>
          {activeItem.isOnSale && !activeItem.isBundle && <Ribbon>SALE</Ribbon>}
        </div>

        {!product.isBundle && (
          <StarRating
            itemName={product.item.displayNameOnPage}
            reviewRating={rating}
            reviewCount={reviewCount}
            showReviewCount
            starsColor='var(--brand-black)'
            fontSize='14px'
            labelClassName='!tw-text-brand-black tw-text-xs'
            linkToReviews={false}
          />
        )}

        <a
          href={itemUrl}
          {...{ rel }}
          className='tw-mt-7 tw-flex tw-h-[240px] tw-w-full tw-items-center tw-justify-center !tw-no-underline [&_img]:tw-h-full [&_img]:tw-max-h-[240px] [&_img]:tw-max-w-full [&_img]:tw-px-2 [&_img]:tw-transition'>
          <PreviewRenderCanvas
            config={activePreview}
            designUrl={activeAsset?.fileUrl || ACTIVE_DESIGN_DEFAULT.url}
            graphicType={graphicType}
            zoom={1}
          />
        </a>

        <p className='tw-mt-3 tw-mb-0 tw-text-center tw-text-sm tw-font-normal tw-text-body'>
          {product.item.tagline}
        </p>

        {product.canMiyo && (
          <div className='tw-mt-5 tw-mb-4 tw-flex tw-w-full tw-items-center tw-justify-center'>
            <MyRTICPersonalize href={`${itemUrl}&design=true`} />
          </div>
        )}

        <div className='tw-mb-6 tw-flex tw-min-h-[52px] tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-1'>
          <p
            className={classNames('tw-mb-0 tw-text-base tw-font-bold', {
              'tw-text-onsale': isDiscounted,
              'tw-text-body': !isDiscounted,
            })}>
            {isDiscounted && <>Now</>} {currentRateFormatted}
          </p>
          {isDiscounted && (
            <p className='tw-mb-0 tw-text-base tw-font-medium tw-text-body tw-line-through'>
              {ourPriceFormatted}
            </p>
          )}
        </div>

        <div className='!tw-mb-4 tw-mt-5 tw-flex tw-justify-center'>
          <ColorSwatches
            showHeader={false}
            colors={product.pickerVariants.colors.slice(0, MAX_SWATCHES_SHOWN)}
            activeColor={activeVariant}
            itemHandler={onSwatchClick}
            wrapperClassName='tw-text-center'
            colorPickerClassName='!tw-w-full'
            indicateLimitedEdition={false}
            indicateClearance={false}
            centered
            swatchesAddendum={
              product.pickerVariants.colors.length > MAX_SWATCHES_SHOWN && (
                <MoreColorsSwatch
                  onMoreClick={() => {
                    window.location.href = itemUrl;
                  }}
                  size={35}
                />
              )
            }
          />
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
